// Pre-anaesthesia check (PAC) clearance — moved into the doctor console
// (Task B) so an anaesthetist reviewing the patient in front of them does not
// have to leave the console and go hunting through the OT PAC queue for the
// matching case.
//
// Reuses the OT api's pac_pending queue and recordPac rather than re-deriving
// their contract, but is NOT built on OT-module UI primitives
// (IpdSectionCard, OtSelect, ...) — this file is the console's own idiom, so
// the small amount of field logic it shares with
// ot/components/panels/PreopPanel.js's PacCard is duplicated here rather than
// imported across modules.
//
// Gated on the ot_pac permission at the section-registry level
// (config/schema/defaultDoctorConsole.config.json) — this component does not
// check permissions itself, it assumes the section would not be visible
// otherwise. Any anaesthetist may record a clearance, not only the one named
// on the case.

import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";

import { fetchPac, fetchPacQueue } from "../../ot/api/otApi";
import { formatIstDateTime } from "../../../utils/istDateTime";
import { LIFECYCLE_LABELS } from "../../ot/constants";
import { canRecordPacOutcome } from "../../ot/utils/pacEligibility";
import PreAnesthesiaAssessmentModal from "../../ot/components/PreAnesthesiaAssessmentModal";

/** The operation this case is booked for, however the case named it. */
function procedureNameOf(otCase) {
  const primary = (otCase.procedures || []).find((p) => p.is_primary);
  return primary?.procedure_name || otCase.procedures?.[0]?.procedure_name || "Surgical Procedure Planned (TBD)";
}

function Field({ label, value }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 11, color: "#94A3B8" }}>{label}</Typography>
      <Typography sx={{ fontSize: 13 }}>{value || "—"}</Typography>
    </Box>
  );
}

function PacCaseCard({ otCase, onRecorded }) {
  const [pac, setPac] = useState(null);
  const [loadingPac, setLoadingPac] = useState(true);
  const [open, setOpen] = useState(false);

  const loadPac = useCallback(async () => {
    setLoadingPac(true);
    try {
      setPac(await fetchPac(otCase.id, Boolean(otCase.is_request)));
    } catch (err) {
      setPac(null);
    } finally {
      setLoadingPac(false);
    }
  }, [otCase.id, otCase.is_request]);

  useEffect(() => {
    loadPac();
  }, [loadPac]);

  const current = pac?.current;
  // Mirrors manage_preop.CasePacView.post / ot_common.PAC_CLOSED_STATUSES —
  // never offer an outcome the server would refuse.
  const canRecord = canRecordPacOutcome(otCase.lifecycle_status);

  return (
    <Box sx={{ p: 2, mb: 2, border: "1px solid #E2E8F0", borderRadius: 1.5 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>
            {otCase.case_number} — {procedureNameOf(otCase)}
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#64748B" }}>
            Surgery {formatIstDateTime(otCase.next_booking_start, "Surgical Booking Schedule Pending")}
          </Typography>
        </Box>
        <Box sx={{ textAlign: "right" }}>
          <Button size="small" variant="contained" disabled={!canRecord} onClick={() => setOpen(true)}>
            Record PAC Evaluation
          </Button>
          {!canRecord && (
            <Typography sx={{ fontSize: 11, color: "#B45309", mt: 0.5 }}>
              PAC evaluation locked while case is in stage: {LIFECYCLE_LABELS[otCase.lifecycle_status] || otCase.lifecycle_status}
            </Typography>
          )}
        </Box>
      </Box>

      {loadingPac ? (
        <CircularProgress size={16} sx={{ mt: 1.5 }} />
      ) : current ? (
        <Box
          sx={{
            mt: 1.5,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(4, 1fr)" },
            gap: 1.5,
          }}
        >
          <Field label="Outcome" value={current.outcome} />
          <Field label="ASA Physical Status" value={current.asa_display} />
          <Field label="Assessed By" value={current.assessed_by_name} />
          <Field label="Assessment Date & Time" value={formatIstDateTime(current.assessed_at)} />
        </Box>
      ) : (
        <Typography sx={{ mt: 1.5, fontSize: 13, color: "#64748B" }}>
          No pre-anesthetic clearance documented yet.
        </Typography>
      )}

      {open && (
        <PreAnesthesiaAssessmentModal
          otCase={otCase}
          onClose={() => setOpen(false)}
          onSaved={() => {
            setOpen(false);
            loadPac();
            onRecorded();
          }}
        />
      )}
    </Box>
  );
}

export default function PacSection({ patientId }) {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // pac_pending=true, search=<patient identifier> -- the existing PAC
      // queue endpoint, scoped to this one patient rather than a new client.
      const res = await fetchPacQueue({ search: patientId });
      setCases(res?.data || []);
    } catch (err) {
      setError(err?.body?.message || err?.message || "Could not load the PAC queue");
      setCases([]);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    if (patientId) load();
    else setLoading(false);
  }, [patientId, load]);

  if (!patientId) {
    return (
      <Typography sx={{ fontSize: 13, color: "#64748B" }}>
        Choose a patient before recording an anaesthetic clearance.
      </Typography>
    );
  }

  if (loading) {
    return <CircularProgress size={18} />;
  }

  if (error) {
    return <Typography sx={{ color: "#B45309", fontSize: 13 }}>{error}</Typography>;
  }

  if (cases.length === 0) {
    return (
      <Typography sx={{ fontSize: 13, color: "#64748B" }}>
        No surgical cases pending pre-anesthesia clearance for this patient.
      </Typography>
    );
  }

  return (
    <Box>
      {cases.map((c) => (
        <PacCaseCard key={c.id} otCase={c} onRecorded={load} />
      ))}
    </Box>
  );
}
